(() => {
  "use strict";

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

  const config = window.el0win || {};
  const locale = document.documentElement.lang || "pt-BR";
  const currency = config.currency || "BRL";
  const currencyFormatter = new Intl.NumberFormat(locale, { style: "currency", currency });
  const money = (cents) => currencyFormatter.format(Number(cents || 0) / 100);

  const memoryStorage = new Map();
  const storage = {
    read(key, fallback = []) {
      try {
        const raw = localStorage.getItem(key);
        if (raw !== null) {
          const value = JSON.parse(raw);
          return value ?? fallback;
        }
      } catch (_) {}
      try {
        const raw = memoryStorage.get(key);
        if (raw !== undefined) {
          const value = JSON.parse(raw);
          return value ?? fallback;
        }
      } catch (_) {}
      return fallback;
    },
    write(key, value) {
      const raw = JSON.stringify(value);
      memoryStorage.set(key, raw);
      try {
        localStorage.setItem(key, raw);
        return true;
      } catch (_) {
        return false;
      }
    },
    get(key) {
      try {
        const value = localStorage.getItem(key);
        if (value !== null) return value;
      } catch (_) {}
      return memoryStorage.get(key) ?? null;
    },
    set(key, value) {
      const normalized = String(value);
      memoryStorage.set(key, normalized);
      try { localStorage.setItem(key, normalized); return true; } catch (_) { return false; }
    }
  };

  const sameOriginUrl = (value, fallback = "#") => {
    try {
      const url = new URL(String(value || ""), location.origin);
      if (url.origin !== location.origin || !["http:", "https:"].includes(url.protocol)) return fallback;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return fallback;
    }
  };

  const safeImageUrl = (value) => {
    try {
      const url = new URL(String(value || ""), location.origin);
      return ["http:", "https:"].includes(url.protocol) ? url.href : "";
    } catch (_) {
      return "";
    }
  };

  const setBodyLock = () => {
    const hasOpenOverlay = ["#MobileNav", "#CartDrawer", "#NewsPopup"].some((selector) => {
      const node = $(selector);
      return node && !node.hidden;
    });
    document.body.classList.toggle("is-locked", hasOpenOverlay);
  };

  let lastOverlayTrigger = null;
  const rememberTrigger = (trigger) => { if (trigger instanceof HTMLElement) lastOverlayTrigger = trigger; };
  const restoreTrigger = () => {
    const trigger = lastOverlayTrigger;
    lastOverlayTrigger = null;
    trigger?.focus?.({ preventScroll: true });
  };

  const notify = (message, type = "info") => {
    const region = $("[data-toast]");
    if (!region) return;
    region.textContent = message;
    region.dataset.type = type;
    region.hidden = false;
    window.clearTimeout(notify.timer);
    notify.timer = window.setTimeout(() => { region.hidden = true; }, 3200);
  };

  const setBusy = (node, busy) => {
    if (!node) return;
    node.toggleAttribute("disabled", busy);
    node.setAttribute("aria-busy", busy ? "true" : "false");
  };

  const openMenu = (trigger) => {
    const menu = $("#MobileNav");
    if (!menu) return;
    rememberTrigger(trigger);
    menu.hidden = false;
    trigger?.setAttribute("aria-expanded", "true");
    setBodyLock();
    $("[data-close-menu]", menu)?.focus({ preventScroll: true });
  };

  const closeMenu = ({ restoreFocus = true } = {}) => {
    const menu = $("#MobileNav");
    if (!menu) return;
    menu.hidden = true;
    $("[data-open-menu]")?.setAttribute("aria-expanded", "false");
    setBodyLock();
    if (restoreFocus) restoreTrigger();
  };

  const toggleSearch = (trigger, force) => {
    const search = $("#SearchDrawer");
    if (!search) return;
    const open = typeof force === "boolean" ? force : search.hidden;
    search.hidden = !open;
    trigger?.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) $("input[type=search]", search)?.focus({ preventScroll: true });
  };

  const getDrawer = () => $("#CartDrawer");
  const setCartTabState = (open) => {
    const cartTab = $("[data-tab-cart]");
    const tabbar = cartTab?.closest(".tabbar");
    if (!cartTab || !tabbar) return;
    if (open) {
      $$("a[aria-current=page]", tabbar).forEach((link) => {
        if (link === cartTab) return;
        link.dataset.previousCurrent = "page";
        link.removeAttribute("aria-current");
      });
      cartTab.setAttribute("aria-current", "page");
    } else {
      if (location.pathname !== (config.cartUrl || "/cart")) cartTab.removeAttribute("aria-current");
      $$('[data-previous-current="page"]', tabbar).forEach((link) => {
        link.setAttribute("aria-current", "page");
        delete link.dataset.previousCurrent;
      });
    }
  };

  const openDrawer = (trigger) => {
    const drawer = getDrawer();
    if (!drawer) return;
    rememberTrigger(trigger);
    drawer.hidden = false;
    setCartTabState(true);
    setBodyLock();
    $("[data-close-drawer]", drawer)?.focus({ preventScroll: true });
  };

  const closeDrawer = ({ restoreFocus = true } = {}) => {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.hidden = true;
    setCartTabState(false);
    setBodyLock();
    if (restoreFocus) restoreTrigger();
  };

  const closePopup = ({ remember = true, restoreFocus = false } = {}) => {
    const popup = $("#NewsPopup");
    if (!popup) return;
    popup.hidden = true;
    if (remember) storage.set("el0win_pop", "1");
    setBodyLock();
    if (restoreFocus) restoreTrigger();
  };

  document.addEventListener("click", (event) => {
    const openMenuButton = event.target.closest("[data-open-menu]");
    if (openMenuButton) {
      event.preventDefault();
      openMenu(openMenuButton);
      return;
    }

    if (event.target.closest("[data-close-menu]")) {
      event.preventDefault();
      closeMenu();
      return;
    }

    const searchButton = event.target.closest("[data-open-search]");
    if (searchButton) {
      event.preventDefault();
      toggleSearch(searchButton);
      return;
    }

    const cartButton = event.target.closest("[data-cart-toggle]");
    if (cartButton && config.cartType === "drawer") {
      event.preventDefault();
      openDrawer(cartButton);
      return;
    }

    if (event.target.closest("[data-close-drawer]")) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.target.closest("[data-close-popup]")) {
      event.preventDefault();
      closePopup({ restoreFocus: false });
      return;
    }

    if (event.target.closest("#MobileNav a")) closeMenu({ restoreFocus: false });
  });

  const visibleDialog = () => {
    const drawer = getDrawer();
    if (drawer && !drawer.hidden) return $(".drawer-panel", drawer);
    const menu = $("#MobileNav");
    if (menu && !menu.hidden) return $(".mobile-nav", menu);
    const popup = $("#NewsPopup");
    if (popup && !popup.hidden) return $("[data-popup-card]", popup);
    return null;
  };

  const trapTabKey = (event, container) => {
    const focusable = $$(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      container
    ).filter((node) => !node.hidden && node.getAttribute("aria-hidden") !== "true");
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  document.addEventListener("keydown", (event) => {
    if (event.key === "Tab") {
      const dialog = visibleDialog();
      if (dialog) trapTabKey(event, dialog);
      return;
    }
    if (event.key !== "Escape") return;
    if (getDrawer() && !getDrawer().hidden) return closeDrawer();
    if ($("#MobileNav") && !$("#MobileNav").hidden) return closeMenu();
    if ($("#NewsPopup") && !$("#NewsPopup").hidden) return closePopup({ restoreFocus: false });
    if ($("#SearchDrawer") && !$("#SearchDrawer").hidden) toggleSearch($("[data-open-search]"), false);
  });

  // Slideshow: isolated per section, pauseable and reduced-motion aware.
  $$("[data-slideshow]").forEach((slideshow) => {
    const slides = $$(".slide", slideshow);
    const dots = $$("[data-dot]", slideshow);
    if (slides.length < 2) return;

    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));
    let timer = null;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", active ? "false" : "true");
      });
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-current", active ? "true" : "false");
      });
    };

    const stop = () => { if (timer) window.clearInterval(timer); timer = null; };
    const start = () => {
      if (prefersReducedMotion || document.hidden || timer) return;
      timer = window.setInterval(() => show(index + 1), 6000);
    };

    dots.forEach((dot) => dot.addEventListener("click", () => {
      show(Number(dot.dataset.dot || 0));
      stop();
      start();
    }));

    let touchStartX = null;
    slideshow.addEventListener("touchstart", (event) => {
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      stop();
    }, { passive: true });
    slideshow.addEventListener("touchend", (event) => {
      if (touchStartX == null || !event.changedTouches.length) return start();
      const delta = event.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) >= 48) show(index + (delta < 0 ? 1 : -1));
      start();
    }, { passive: true });
    slideshow.addEventListener("touchcancel", () => { touchStartX = null; start(); }, { passive: true });

    slideshow.addEventListener("mouseenter", stop);
    slideshow.addEventListener("mouseleave", start);
    slideshow.addEventListener("focusin", stop);
    slideshow.addEventListener("focusout", start);
    document.addEventListener("visibilitychange", () => document.hidden ? stop() : start());
    show(index);
    start();
  });

  // Product media gallery: desktop active slide + mobile native swipe.
  const gallery = $("[data-gallery]");
  if (gallery) {
    const viewport = $("[data-gallery-viewport]", gallery) || gallery;
    const slides = $$(".gallery-slide", gallery);
    const thumbs = $$("[data-thumb]", gallery);
    const galleryCount = $("[data-gallery-count]", gallery);

    const activateMedia = (index, { scroll = false } = {}) => {
      slides.forEach((slide, i) => slide.classList.toggle("is-active", i === index));
      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-current", active ? "true" : "false");
      });
      if (galleryCount) galleryCount.textContent = `${index + 1} / ${slides.length}`;
      if (scroll && window.matchMedia("(max-width: 749px)").matches) {
        slides[index]?.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "nearest", inline: "start" });
      }
    };

    thumbs.forEach((button) => button.addEventListener("click", () => activateMedia(Number(button.dataset.thumb || 0), { scroll: true })));

    if ("IntersectionObserver" in window && window.matchMedia("(max-width: 749px)").matches && viewport !== gallery) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const index = slides.indexOf(visible.target);
        if (index >= 0) activateMedia(index);
      }, { root: viewport, threshold: [0.55, 0.8] });
      slides.forEach((slide) => observer.observe(slide));
    }

    $$(".gallery-slide img", gallery).forEach((image) => image.addEventListener("click", () => {
      if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) image.classList.toggle("is-zoom");
    }));
  }

  const productForm = $("[data-product-form]");
  const productJson = $("[data-product-json]");
  if (productForm && productJson) {
    let product = {};
    try { product = JSON.parse(productJson.textContent || "{}"); } catch (_) { product = {}; }
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const idInput = $("[data-variant-id]", productForm);
    const atc = $("[data-atc]", productForm);
    const stickyAtc = $("[data-sticky-submit]");
    const stickyBar = $("[data-sticky-atc]");
    const priceRoot = $("[data-product-price]");
    const sku = $("[data-product-sku]");
    const availability = $(".pdp-availability");
    const availabilityText = availability ? $("span:last-child", availability) : null;
    const productError = $("[data-product-error]", productForm);

    const showProductError = (message) => {
      if (!productError) return;
      productError.textContent = message || "Não foi possível adicionar o item. Tente novamente.";
      productError.hidden = false;
    };

    const renderVariantPrice = (variant) => {
      if (!priceRoot || !variant) return;
      const now = $("[data-price-now]", priceRoot);
      const was = $("[data-price-was]", priceRoot);
      const pix = $("[data-pix-price]", priceRoot);
      const installment = $("[data-installment-price]", priceRoot);
      const sale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
      priceRoot.querySelector(".price")?.classList.toggle("is-sale", sale);
      if (now) now.textContent = money(variant.price);
      if (was) {
        was.hidden = !sale;
        was.textContent = sale ? money(variant.compare_at_price) : "";
      }
      if (pix) pix.textContent = money(Math.round(Number(variant.price || 0) * (100 - Number(config.pixPercent || 0)) / 100));
      if (installment) installment.textContent = money(Math.round(Number(variant.price || 0) / Math.max(1, Number(config.installments || 1))));
    };

    const selectVariant = () => {
      const optionInputs = $$("input[type=radio]", productForm);
      const selected = optionInputs.filter((input) => input.checked).map((input) => input.value);
      const variant = optionInputs.length
        ? variants.find((candidate) => (candidate.options || []).every((option, index) => option === selected[index]))
        : variants.find((candidate) => String(candidate.id) === String(idInput?.value || "")) || variants[0];
      if (!variant || !idInput) {
        if (atc) { atc.disabled = true; atc.textContent = config.unavailableLabel || "Indisponível"; }
        if (stickyAtc) stickyAtc.disabled = true;
        if (stickyBar) stickyBar.classList.add("is-disabled");
        if (availability) availability.classList.add("is-unavailable");
        if (availabilityText) availabilityText.textContent = config.unavailableLabel || "Indisponível";
        return null;
      }

      idInput.value = variant.id;
      if (atc) {
        atc.disabled = !variant.available;
        atc.textContent = variant.available ? (config.addLabel || "Adicionar") : (config.soldOutLabel || "Esgotado");
      }
      if (stickyAtc) {
        stickyAtc.disabled = !variant.available;
        stickyAtc.textContent = variant.available ? (config.addLabel || "Adicionar") : (config.soldOutLabel || "Esgotado");
      }
      if (stickyBar) stickyBar.classList.toggle("is-disabled", !variant.available);
      if (availability) availability.classList.toggle("is-unavailable", !variant.available);
      if (availabilityText) availabilityText.textContent = variant.available ? "Disponível para compra" : "Indisponível no momento";
      $$('fieldset.option', productForm).forEach((fieldset) => {
        const checked = $('input[type="radio"]:checked', fieldset);
        const selectedLabel = $('legend small', fieldset);
        if (checked && selectedLabel) selectedLabel.textContent = checked.value;
      });
      const stickyPrice = $("[data-sticky-price]");
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
      if (sku) {
        sku.hidden = !variant.sku;
        $("[data-sku-value]", sku)?.replaceChildren(document.createTextNode(variant.sku || ""));
      }
      renderVariantPrice(variant);

      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (mediaId && gallery) {
        const slides = $$(".gallery-slide", gallery);
        const hit = slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
        if (hit >= 0) $("[data-thumb=\"" + hit + "\"]", gallery)?.click();
      }

      const url = new URL(location.href);
      url.searchParams.set("variant", String(variant.id));
      history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      if (productError) productError.hidden = true;
      return variant;
    };

    productForm.addEventListener("change", () => {
      selectVariant();
    });
    selectVariant();

    productForm.addEventListener("submit", async (event) => {
      if (config.cartType !== "drawer") return;
      event.preventDefault();
      setBusy(atc, true);
      if (productError) productError.hidden = true;
      const body = new FormData(productForm);
      const ids = cartSectionIds();
      if (ids.length) body.append("sections", ids.join(","));
      body.append("sections_url", location.pathname);
      try {
        const response = await fetch(`${config.cartAdd || "/cart/add"}.js`, {
          method: "POST",
          body,
          headers: { Accept: "application/json" }
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data?.description || data?.message || "Não foi possível adicionar o item.");
        replaceSections(data.sections);
        await refreshCartSummary();
        openDrawer(atc);
        notify(config.addedLabel || "Produto adicionado à sacola.", "success");
      } catch (error) {
        const message = error?.message || "Não foi possível adicionar o item.";
        const selectedVariant = selectVariant();
        if (selectedVariant && selectedVariant.available) {
          showProductError("O produto está disponível, mas não foi possível adicionar à sacola agora. Tente novamente.");
        } else {
          showProductError(message);
        }
        setBusy(atc, false);
        return;
      }
      setBusy($("[data-atc]", productForm), false);
    });

    $("[data-sticky-submit]")?.addEventListener("click", () => productForm.requestSubmit());

    const atcRow = $(".atc-row", productForm);
    if (stickyBar && atcRow) {
      const mobileQuery = window.matchMedia("(max-width: 749px)");
      const updateStickyBar = () => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const rect = atcRow.getBoundingClientRect();
        const rowVisible = rect.top < viewportHeight - 20 && rect.bottom > 20;
        const beforePrimaryAction = rect.top > viewportHeight * 0.88;
        const footerVisible = document.body.classList.contains("footer-in-view");
        const unavailable = stickyBar.classList.contains("is-disabled");
        const shouldHide = !mobileQuery.matches || footerVisible || unavailable || rowVisible || beforePrimaryAction;
        stickyBar.classList.toggle("is-hidden", shouldHide);
      };
      updateStickyBar();
      window.addEventListener("scroll", updateStickyBar, { passive: true });
      window.addEventListener("resize", updateStickyBar);
      mobileQuery.addEventListener?.("change", updateStickyBar);
      const classObserver = new MutationObserver(updateStickyBar);
      classObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    }
  }

  function cartSectionIds() {
    const ids = [];
    if (config.cartType === "drawer") ids.push("cart-drawer");
    const cartPage = $("[data-cart-page][data-section-id]");
    if (cartPage?.dataset.sectionId) ids.push(cartPage.dataset.sectionId);
    return [...new Set(ids)].slice(0, 5);
  }

  function replaceSections(sections) {
    if (!sections || typeof sections !== "object") return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, "text/html");
      const replacement = doc.getElementById(`shopify-section-${id}`);
      const current = document.getElementById(`shopify-section-${id}`);
      if (replacement && current) current.replaceWith(replacement);
    });
  }

  async function refreshCartSummary() {
    try {
      const response = await fetch(`${config.cartUrl || "/cart"}.js`, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("cart");
      const cart = await response.json();
      $$("[data-cart-count]").forEach((node) => {
        node.textContent = cart.item_count > 0 ? String(cart.item_count) : "";
        node.hidden = cart.item_count < 1;
      });
      $$("[data-cart-subtotal]").forEach((subtotal) => { subtotal.textContent = money(cart.total_price); });
      return cart;
    } catch (_) {
      return null;
    }
  }

  async function updateCartLine(line, quantity) {
    if (!line || line.dataset.updating === "true") return;
    const drawerWasOpen = Boolean(getDrawer() && !getDrawer().hidden);
    const previousScrollTop = drawerWasOpen ? ($("[data-cart-scroll]", getDrawer())?.scrollTop || 0) : 0;
    line.dataset.updating = "true";
    line.setAttribute("aria-busy", "true");
    $$("button,input", line).forEach((control) => control.disabled = true);
    try {
      const ids = cartSectionIds();
      const response = await fetch(`${config.cartChange || "/cart/change"}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          id: line.dataset.key,
          quantity: Math.max(0, Number(quantity || 0)),
          sections: ids.join(","),
          sections_url: location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || "Não foi possível atualizar a sacola.");
      replaceSections(data.sections);
      if (drawerWasOpen) {
        const drawer = getDrawer();
        if (drawer) {
          drawer.hidden = false;
          setBodyLock();
          const scrollArea = $("[data-cart-scroll]", drawer);
          if (scrollArea) scrollArea.scrollTop = previousScrollTop;
        }
      }
      await refreshCartSummary();
      if (document.contains(line)) {
        line.dataset.updating = "false";
        line.removeAttribute("aria-busy");
        $$("button,input", line).forEach((control) => control.disabled = false);
      }
    } catch (error) {
      line.dataset.updating = "false";
      line.removeAttribute("aria-busy");
      $$("button,input", line).forEach((control) => control.disabled = false);
      notify(error?.message || "Não foi possível atualizar a sacola.", "error");
    }
  }

  document.addEventListener("click", async (event) => {
    const quickAdd = event.target.closest("[data-quick-add]");
    if (quickAdd) {
      event.preventDefault();
      setBusy(quickAdd, true);
      try {
        const ids = cartSectionIds();
        const response = await fetch(`${config.cartAdd || "/cart/add"}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: [{ id: Number(quickAdd.dataset.quickAdd), quantity: 1 }],
            sections: ids.join(","),
            sections_url: location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data?.description || data?.message || "Não foi possível adicionar o item.");
        replaceSections(data.sections);
        await refreshCartSummary();
        if (config.cartType === "drawer") openDrawer(quickAdd);
        else location.assign(config.cartUrl || "/cart");
        notify(config.addedLabel || "Produto adicionado à sacola.", "success");
      } catch (error) {
        notify(error?.message || "Não foi possível adicionar o item.", "error");
      } finally {
        if (document.contains(quickAdd)) setBusy(quickAdd, false);
      }
      return;
    }

    const change = event.target.closest("[data-qty-change]");
    if (change) {
      const line = change.closest(".cart-line");
      const input = line ? $("[data-qty]", line) : null;
      if (!line || !input) return;
      const next = Math.max(0, Number(input.value || 0) + Number(change.dataset.qtyChange || 0));
      input.value = String(next);
      await updateCartLine(line, next);
      return;
    }

    const remove = event.target.closest("[data-qty-remove]");
    if (remove) {
      event.preventDefault();
      await updateCartLine(remove.closest(".cart-line"), 0);
    }
  });

  document.addEventListener("change", (event) => {
    const input = event.target.closest("[data-qty]");
    if (!input) return;
    const line = input.closest(".cart-line");
    if (!line) return;
    const quantity = Math.max(0, Math.floor(Number(input.value || 0)));
    input.value = String(quantity);
    updateCartLine(line, quantity);
  });

  let noteTimer;
  document.addEventListener("input", (event) => {
    const note = event.target.closest("[data-cart-note]");
    if (!note) return;
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(async () => {
      try {
        const response = await fetch(`${config.cartUrl || "/cart"}/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ note: note.value })
        });
        if (!response.ok) throw new Error("note");
      } catch (_) {
        notify("Não foi possível salvar a observação agora.", "error");
      }
    }, 450);
  });

  // Product recommendations via Section Rendering API response HTML.
  $$("product-recs[data-url]").forEach(async (recs) => {
    try {
      const response = await fetch(recs.dataset.url, { headers: { Accept: "text/html" } });
      if (!response.ok) throw new Error("recommendations");
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const section = doc.querySelector(".page-width.section");
      if (section) recs.replaceWith(section);
      else recs.remove();
    } catch (_) {
      recs.remove();
    }
  });

  // Countdown with invalid-date protection and automatic stop at zero.
  $$("[data-countdown]").forEach((element) => {
    const end = Date.parse(element.dataset.countdown || "");
    if (!Number.isFinite(end)) {
      element.hidden = true;
      return;
    }
    let timer;
    const tick = () => {
      const diff = Math.max(0, end - Date.now());
      const values = {
        d: Math.floor(diff / 86400000),
        h: Math.floor(diff / 3600000) % 24,
        m: Math.floor(diff / 60000) % 60,
        s: Math.floor(diff / 1000) % 60
      };
      Object.entries(values).forEach(([key, value]) => {
        const node = element.querySelector(`[data-${key}]`);
        if (node) node.textContent = String(value).padStart(2, "0");
      });
      if (diff === 0 && timer) window.clearInterval(timer);
    };
    tick();
    if (end > Date.now()) timer = window.setInterval(tick, 1000);
  });

  const makeStoredCard = (item, { removable = false } = {}) => {
    const article = document.createElement("article");
    article.className = "card";
    const mediaLink = document.createElement("a");
    mediaLink.className = "card-media";
    mediaLink.href = sameOriginUrl(item.url);
    const imageUrl = safeImageUrl(item.img);
    if (imageUrl) {
      const image = document.createElement("img");
      image.src = imageUrl;
      image.alt = item.title || "";
      image.width = 400;
      image.height = 500;
      image.loading = "lazy";
      image.decoding = "async";
      mediaLink.append(image);
    }
    const body = document.createElement("div");
    body.className = "card-body";
    const title = document.createElement("h3");
    title.className = "card-title";
    const titleLink = document.createElement("a");
    titleLink.href = sameOriginUrl(item.url);
    titleLink.textContent = item.title || "Produto";
    title.append(titleLink);
    body.append(title);
    if (item.price) {
      const price = document.createElement("p");
      price.className = "price-now";
      price.textContent = item.price;
      body.append(price);
    }
    if (removable) {
      const remove = document.createElement("button");
      remove.className = "btn btn-quick";
      remove.type = "button";
      remove.dataset.wishRemove = item.handle || "";
      remove.textContent = "Remover";
      body.append(remove);
    }
    article.append(mediaLink, body);
    return article;
  };

  const recentSave = $("[data-recent-save]");
  if (recentSave?.dataset.recentSave) {
    const item = {
      handle: recentSave.dataset.recentSave,
      title: recentSave.dataset.recentTitle || "",
      url: sameOriginUrl(recentSave.dataset.recentUrl),
      img: safeImageUrl(recentSave.dataset.recentImg),
      price: recentSave.dataset.recentPrice || ""
    };
    const list = storage.read("el0win_recent", []).filter((entry) => entry && entry.handle !== item.handle);
    storage.write("el0win_recent", [item, ...list].slice(0, 8));
  }

  const recentGrid = $("[data-recent-grid]");
  if (recentGrid) {
    const list = storage.read("el0win_recent", []).filter((entry) => entry && entry.handle);
    const currentHandle = $("[data-recent-save]")?.dataset.recentSave;
    const visible = list.filter((entry) => entry.handle !== currentHandle).slice(0, 8);
    if (!visible.length) recentGrid.closest("section")?.remove();
    else recentGrid.replaceChildren(...visible.map((item) => makeStoredCard(item)));
  }

  // Predictive search with request cancellation and DOM-safe rendering.
  const predict = $("[data-predict]");
  const queryInput = $("#q");
  let searchTimer;
  let searchController;
  queryInput?.addEventListener("input", () => {
    window.clearTimeout(searchTimer);
    searchController?.abort();
    const term = queryInput.value.trim();
    if (!predict || term.length < 2) {
      if (predict) { predict.hidden = true; predict.replaceChildren(); }
      queryInput.setAttribute("aria-expanded", "false");
      return;
    }

    searchTimer = window.setTimeout(async () => {
      searchController = new AbortController();
      try {
        const base = config.predictiveSearchUrl || `${config.searchUrl || "/search"}/suggest.json`;
        const url = `${base}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=5`;
        const response = await fetch(url, { signal: searchController.signal, headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error("search");
        const data = await response.json();
        const items = data?.resources?.results?.products || [];
        predict.replaceChildren(...items.map((product) => {
          const link = document.createElement("a");
          link.href = sameOriginUrl(product.url);
          link.setAttribute("role", "option");
          link.textContent = product.title || "Produto";
          return link;
        }));
        predict.hidden = items.length === 0;
        queryInput.setAttribute("aria-expanded", items.length ? "true" : "false");
      } catch (error) {
        if (error?.name !== "AbortError") {
          predict.hidden = true;
          queryInput.setAttribute("aria-expanded", "false");
        }
      }
    }, 220);
  });

  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (_) {}
    }
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.setAttribute("aria-hidden", "true");
    field.style.position = "fixed";
    field.style.left = "-9999px";
    field.style.top = "0";
    document.body.append(field);
    field.focus({ preventScroll: true });
    field.select();
    field.setSelectionRange(0, field.value.length);
    let copied = false;
    try { copied = document.execCommand("copy"); } catch (_) { copied = false; }
    field.remove();
    return copied;
  };

  const shareUrl = () => {
    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    return canonical || location.href;
  };

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-share]");
    if (!button) return;
    event.preventDefault();
    const url = shareUrl();
    const payload = { title: document.title, text: document.title, url };
    const original = button.dataset.shareLabel || button.textContent.trim() || "Compartilhar";
    button.dataset.shareLabel = original;

    if (navigator.share) {
      try {
        await navigator.share(payload);
        notify("Compartilhamento aberto.", "success");
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    const copied = await copyText(url);
    if (copied) {
      button.textContent = "Link copiado ✓";
      notify("Link do produto copiado.", "success");
      window.setTimeout(() => { button.textContent = original; }, 1800);
    } else {
      button.textContent = "Copie o link";
      notify("Não foi possível abrir o compartilhamento. Copie o endereço da página.", "error");
      window.setTimeout(() => { button.textContent = original; }, 2200);
    }
  });

  $$("[data-pdp-qty]").forEach((button) => button.addEventListener("click", () => {
    const input = $("#qty");
    if (!input) return;
    const next = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(button.dataset.pdpQty || 0));
    input.value = String(next);
  }));

  const popup = $("#NewsPopup");
  if (popup && !storage.get("el0win_pop")) {
    window.setTimeout(() => {
      if (storage.get("el0win_pop")) return;
      popup.hidden = false;
      setBodyLock();
      $("input[type=email]", popup)?.focus({ preventScroll: true });
    }, Number(config.popupDelay || 8000));
  }

  // Optional custom privacy banner backed by Shopify's Customer Privacy API.
  const cookieBar = $("#CookieBar");
  if (cookieBar && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      if (error || !window.Shopify.customerPrivacy) return;
      try { cookieBar.hidden = !window.Shopify.customerPrivacy.shouldShowBanner(); } catch (_) {}
    });
    cookieBar.addEventListener("click", (event) => {
      const button = event.target.closest("[data-cookie-consent]");
      if (!button || !window.Shopify?.customerPrivacy) return;
      const allow = button.dataset.cookieConsent === "accept";
      setBusy(button, true);
      window.Shopify.customerPrivacy.setTrackingConsent({
        analytics: allow,
        marketing: allow,
        preferences: allow
      }, () => {
        cookieBar.hidden = true;
        setBusy(button, false);
      });
    });
  }

  // Legacy customer-account address country/province selectors.
  $$("[data-country-select]").forEach((country) => {
    const form = country.closest("form");
    const province = form?.querySelector("[data-province-select]");
    const wrap = province?.closest("[data-province-wrap]");
    if (!province) return;

    const defaultCountry = country.dataset.default || "";
    if (defaultCountry && Array.from(country.options).some((option) => option.value === defaultCountry)) {
      country.value = defaultCountry;
    }

    const syncProvinces = () => {
      const raw = country.selectedOptions[0]?.dataset.provinces || "[]";
      let provinces = [];
      try { provinces = JSON.parse(raw); } catch (_) { provinces = []; }
      const wanted = province.dataset.default || "";
      province.replaceChildren(...provinces.map((entry) => {
        const option = document.createElement("option");
        option.value = String(entry[0] || "");
        option.textContent = String(entry[1] || entry[0] || "");
        return option;
      }));
      if (wanted && Array.from(province.options).some((option) => option.value === wanted)) province.value = wanted;
      if (wrap) wrap.hidden = provinces.length === 0;
    };

    country.addEventListener("change", () => { province.dataset.default = ""; syncProvinces(); });
    syncProvinces();
  });

  const footer = $(".site-footer");
  if (footer && "IntersectionObserver" in window) {
    const footerObserver = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      document.body.classList.toggle("footer-in-view", visible);
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.02 });
    footerObserver.observe(footer);
  }

  const topButton = $("[data-to-top]");
  const updateTopButton = () => { if (topButton) topButton.hidden = window.scrollY < 500; };
  updateTopButton();
  window.addEventListener("scroll", updateTopButton, { passive: true });
  topButton?.addEventListener("click", () => window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" }));

  const wishKey = "el0win_wish";
  const wishRead = () => storage.read(wishKey, []).filter((item) => item && item.handle).slice(0, 48);
  const updateWishButtons = (list) => {
    const handles = new Set(list.map((item) => item.handle));
    $$("[data-wish]").forEach((button) => {
      const active = handles.has(button.dataset.handle);
      button.classList.toggle("is-on", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
      if (button.classList.contains("pdp-secondary-btn")) {
        const baseLabel = button.dataset.wishLabel || "Favoritar";
        button.dataset.wishLabel = baseLabel;
        button.textContent = active ? "Favoritado ✓" : baseLabel;
        button.setAttribute("aria-label", active ? "Remover dos favoritos" : "Adicionar aos favoritos");
      }
    });
    $$("[data-wish-count]").forEach((node) => {
      node.textContent = String(list.length);
      node.hidden = list.length < 1;
    });
  };
  const wishWrite = (list) => {
    const normalized = list.filter((item) => item && item.handle).slice(0, 48);
    storage.write(wishKey, normalized);
    updateWishButtons(normalized);
    return normalized;
  };

  updateWishButtons(wishRead());

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-wish]");
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      const item = {
        handle: button.dataset.handle || "",
        title: button.dataset.title || "",
        url: sameOriginUrl(button.dataset.url),
        img: safeImageUrl(button.dataset.img),
        price: button.dataset.price || ""
      };
      let list = wishRead();
      list = list.some((entry) => entry.handle === item.handle)
        ? list.filter((entry) => entry.handle !== item.handle)
        : [item, ...list.filter((entry) => entry.handle !== item.handle)];
      const saved = list.some((entry) => entry.handle === item.handle);
      wishWrite(list);
      notify(saved ? "Produto adicionado aos favoritos." : "Produto removido dos favoritos.", "success");
      return;
    }

    const remove = event.target.closest("[data-wish-remove]");
    if (remove) {
      const list = wishWrite(wishRead().filter((item) => item.handle !== remove.dataset.wishRemove));
      renderWishlist(list);
    }
  });

  const wishGrid = $("[data-wish-grid]");
  const renderWishlist = (list) => {
    if (!wishGrid) return;
    if (!list.length) {
      const empty = document.createElement("p");
      empty.className = "muted";
      empty.textContent = "Sua lista está vazia.";
      wishGrid.replaceChildren(empty);
      return;
    }
    wishGrid.replaceChildren(...list.map((item) => makeStoredCard(item, { removable: true })));
  };
  if (wishGrid) renderWishlist(wishRead());
})();
